const NAMES = [
  '0', '1/16', '1/8', '3/16', '1/4', '5/16', '3/8', '7/16', '1/2',
  '9/16', '5/8', '11/16', '3/4', '13/16', '7/8', '15/16', '1',
] as const;

interface Props {
  readonly picked: number | null;
  readonly answer: number;
  readonly revealed: boolean;
  readonly onPick: (tick: number) => void;
}

/**
 * A rule marked in sixteenths.
 *
 * The visible tick is thin, but each one carries a 30px-wide invisible hit
 * area — a 2px target is unhittable with a thumb, let alone a gloved one.
 */
export function Ruler({ picked, answer, revealed, onPick }: Props) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div
        style={{
          position: 'relative',
          height: 132,
          borderRadius: 12,
          background: 'var(--surface-3)',
          border: '1px solid var(--line-strong)',
          overflow: 'hidden',
        }}
      >
        <div style={{ position: 'absolute', left: 18, right: 18, top: 16, bottom: 42 }}>
          {Array.from({ length: 17 }, (_, i) => {
            const major = i % 4 === 0;
            const mid = i % 2 === 0;
            const isPicked = picked === i;
            const isAnswer = revealed && i === answer;
            const isWrong = revealed && isPicked && i !== answer;

            const colour = isAnswer
              ? 'var(--lime)'
              : isWrong
                ? 'var(--red)'
                : isPicked
                  ? 'var(--lime)'
                  : revealed
                    ? 'rgba(255,255,255,.25)'
                    : 'rgba(255,255,255,.5)';

            /*
             * Labels get their own colour. The tick marks can fade back once
             * the answer is showing — they are decoration at that point — but
             * "1/4" and "3/4" are text, and dimming them to match the ticks
             * put them at 2:1 against the rule, well under AA.
             */
            const labelColour = isAnswer
              ? 'var(--lime)'
              : isWrong
                ? 'var(--red)'
                : isPicked
                  ? 'var(--lime)'
                  : 'var(--text-2)';

            return (
              <button
                key={i}
                type="button"
                onClick={() => onPick(i)}
                disabled={revealed}
                aria-label={`${NAMES[i]} inch`}
                aria-pressed={isPicked}
                style={{
                  position: 'absolute',
                  left: `${(i / 16) * 100}%`,
                  top: 0,
                  bottom: 0,
                  width: 30,
                  marginLeft: -15,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                  background: 'transparent',
                  border: 'none',
                  padding: 0,
                  cursor: revealed ? 'default' : 'pointer',
                }}
              >
                <div
                  style={{
                    width: isPicked || isAnswer ? 3 : 2,
                    height: major ? '100%' : mid ? '68%' : '44%',
                    background: colour,
                    borderRadius: 2,
                    transition: 'background var(--motion), width var(--motion)',
                  }}
                />
                {major && (
                  <div
                    style={{
                      position: 'absolute',
                      bottom: -22,
                      font: '600 11.5px/1 var(--font-display)',
                      color: labelColour,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {NAMES[i]}
                  </div>
                )}
              </button>
            );
          })}
        </div>
        <div
          style={{
            position: 'absolute',
            left: 18,
            right: 18,
            bottom: 38,
            height: 2,
            background: 'rgba(255,255,255,.35)',
          }}
        />
      </div>
      <div style={{ font: '400 12.5px/1.4 var(--font-body)', color: 'var(--text-4)' }}>
        Sixteenths. Tap a line.
      </div>
    </div>
  );
}
